import {
  DEFAULT_PRIORITY_COLORS,
  DEFAULT_REMINDER_PREFERENCES,
  DEFAULT_TASK_SWIPE_ACTIONS,
  DEFAULT_WORKFLOW_COLUMNS,
  SCHEMA_VERSION,
} from '@portfolio-pm/shared';
import * as Y from 'yjs';

/**
 * Top-level keys on the root Y.Doc.
 *
 * The Yjs document is a keyed set of Y.Maps (field-level conflict resolution
 * lives inside each entry's inner Y.Map). Keeping the list in one place lets
 * createDoc seed them all consistently and migrations iterate them safely.
 */
export const ROOT_KEYS = [
  'meta',
  'areas',
  'projects',
  'initiatives',
  'objectives',
  'keyResults',
  'checkIns',
  'tasks',
  'workflowColumns',
  'smartLists',
  'taskArrangements',
  'selectedTaskArrangements',
  'taskActivities',
  'dailyCapacities',
  'protectedBlocks',
  'fieldEvidence',
  'progressEvents',
  'enrichmentEvents',
  'triageDispositions',
  'planningRuns',
  'planSteps',
  'scheduleBlocks',
  'approvalRequests',
  'automationPolicies',
  'executionRuns',
  'executionReceipts',
  'contextFacts',
  'reviewReports',
  'reminderPreferences',
  'uiPreferences',
  'deleted',
] as const;

export type RootKey = (typeof ROOT_KEYS)[number];

/** Options shape used by write helpers that need a deterministic clock. */
export interface WithNow {
  /** Timestamp in ms. Defaults to Date.now() when omitted. */
  now?: number;
}

export interface CreateDocOptions extends WithNow {
  /** Attach the root structures to an existing Y.Doc instead of creating one. */
  doc?: Y.Doc;
}

/**
 * Create (or initialise) a root Portfolio PM Y.Doc.
 *
 * Idempotent: re-running on an already-seeded doc leaves existing meta untouched.
 */
export function createDoc(options: CreateDocOptions = {}): Y.Doc {
  const doc = options.doc ?? new Y.Doc();

  // Touching `doc.getMap(name)` creates the top-level type if missing.
  for (const key of ROOT_KEYS) {
    doc.getMap(key);
  }

  const meta = doc.getMap('meta');
  if (!meta.has('schemaVersion')) {
    const now = options.now ?? Date.now();
    meta.set('schemaVersion', SCHEMA_VERSION);
    meta.set('createdAt', now);
  }

  ensureReminderPreferences(doc, options.now);
  ensureUiPreferences(doc, options.now);
  ensureDefaultWorkflowColumns(doc, options.now);

  return doc;
}

// ─── Typed accessors ──────────────────────────────────────────────────────

export function getMetaMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap('meta');
}

export function getAreasMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('areas');
}

export function getProjectsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('projects');
}

export function getInitiativesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('initiatives');
}

export function getObjectivesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('objectives');
}

export function getKeyResultsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('keyResults');
}

export function getCheckInsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('checkIns');
}

export function getTasksMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('tasks');
}

export function getWorkflowColumnsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('workflowColumns');
}

export function getSmartListsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('smartLists');
}

export function getTaskArrangementsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('taskArrangements');
}

export function getSelectedTaskArrangementsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('selectedTaskArrangements');
}

export function getTaskActivitiesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('taskActivities');
}

export function getDailyCapacitiesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('dailyCapacities');
}

export function getProtectedBlocksMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('protectedBlocks');
}

export function getFieldEvidenceMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('fieldEvidence');
}

export function getProgressEventsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('progressEvents');
}

export function getEnrichmentEventsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('enrichmentEvents');
}

export function getTriageDispositionsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('triageDispositions');
}

export function getPlanningRunsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('planningRuns');
}

export function getPlanStepsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('planSteps');
}

export function getScheduleBlocksMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('scheduleBlocks');
}

export function getApprovalRequestsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('approvalRequests');
}

export function getAutomationPoliciesMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('automationPolicies');
}

export function getExecutionRunsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('executionRuns');
}

export function getExecutionReceiptsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('executionReceipts');
}

export function getContextFactsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('contextFacts');
}

export function getReviewReportsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap('reviewReports');
}

export function getReminderPreferencesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap('reminderPreferences');
}

export function getUiPreferencesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap('uiPreferences');
}

export function getDeletedMap(doc: Y.Doc): Y.Map<number> {
  return doc.getMap('deleted');
}

export function getSchemaVersion(doc: Y.Doc): number | undefined {
  const v = getMetaMap(doc).get('schemaVersion');
  return typeof v === 'number' ? v : undefined;
}

export function ensureReminderPreferences(doc: Y.Doc, now = Date.now()): void {
  const reminderPreferences = getReminderPreferencesMap(doc);

  if (!reminderPreferences.has('dueNudgesEnabled')) {
    reminderPreferences.set('dueNudgesEnabled', DEFAULT_REMINDER_PREFERENCES.dueNudgesEnabled);
  }
  if (!reminderPreferences.has('dueLeadMinutes')) {
    reminderPreferences.set('dueLeadMinutes', DEFAULT_REMINDER_PREFERENCES.dueLeadMinutes);
  }
  if (!reminderPreferences.has('scheduledNudgesEnabled')) {
    reminderPreferences.set(
      'scheduledNudgesEnabled',
      DEFAULT_REMINDER_PREFERENCES.scheduledNudgesEnabled,
    );
  }
  if (!reminderPreferences.has('scheduledLeadMinutes')) {
    reminderPreferences.set(
      'scheduledLeadMinutes',
      DEFAULT_REMINDER_PREFERENCES.scheduledLeadMinutes,
    );
  }
  if (!reminderPreferences.has('createdAt')) {
    reminderPreferences.set('createdAt', now);
  }
  if (!reminderPreferences.has('updatedAt')) {
    reminderPreferences.set('updatedAt', now);
  }
}

export function ensureUiPreferences(doc: Y.Doc, now = Date.now()): void {
  const uiPreferences = getUiPreferencesMap(doc);

  if (!uiPreferences.has('priorityColors')) {
    uiPreferences.set('priorityColors', { ...DEFAULT_PRIORITY_COLORS });
  } else {
    const raw = uiPreferences.get('priorityColors');
    const priorityColors = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const missingColor = Object.keys(DEFAULT_PRIORITY_COLORS).some(
      (key) => typeof priorityColors[key] !== 'string',
    );
    if (missingColor) {
      uiPreferences.set('priorityColors', { ...DEFAULT_PRIORITY_COLORS, ...priorityColors });
    }
  }
  if (!uiPreferences.has('tagColors')) {
    uiPreferences.set('tagColors', {});
  }
  if (!uiPreferences.has('taskSwipeLeftToRight')) {
    uiPreferences.set('taskSwipeLeftToRight', DEFAULT_TASK_SWIPE_ACTIONS.leftToRight);
  }
  if (!uiPreferences.has('taskSwipeRightToLeft')) {
    uiPreferences.set('taskSwipeRightToLeft', DEFAULT_TASK_SWIPE_ACTIONS.rightToLeft);
  }
  if (!uiPreferences.has('favoriteShortcuts')) {
    uiPreferences.set('favoriteShortcuts', []);
  }
  if (!uiPreferences.has('projectSidebarSort')) {
    uiPreferences.set('projectSidebarSort', 'alphabetical');
  }
  if (!uiPreferences.has('projectSidebarCount')) {
    uiPreferences.set('projectSidebarCount', 'active');
  }
  if (!uiPreferences.has('projectSidebarShowArchived')) {
    uiPreferences.set('projectSidebarShowArchived', false);
  }
  if (!uiPreferences.has('appBadgeCountMode')) {
    uiPreferences.set('appBadgeCountMode', 'today');
  }
  if (!uiPreferences.has('createdAt')) {
    uiPreferences.set('createdAt', now);
  }
  if (!uiPreferences.has('updatedAt')) {
    uiPreferences.set('updatedAt', now);
  }
}

/**
 * Wipe all user content in a single CRDT transaction, leaving the doc in the
 * same state as a freshly created one (before seeding).
 * ReminderPreferences and UiPreferences are preserved — they are device-level
 * settings, not workspace content.
 * Call seedInbox() and ensureDefaultWorkflowColumns() after this to restore
 * the minimal defaults.
 */
export function clearContent(doc: Y.Doc): void {
  doc.transact(() => {
    getTasksMap(doc).clear();
    getTaskActivitiesMap(doc).clear();
    getDailyCapacitiesMap(doc).clear();
    getProtectedBlocksMap(doc).clear();
    getFieldEvidenceMap(doc).clear();
    getProgressEventsMap(doc).clear();
    getEnrichmentEventsMap(doc).clear();
    getTriageDispositionsMap(doc).clear();
    getPlanningRunsMap(doc).clear();
    getPlanStepsMap(doc).clear();
    getScheduleBlocksMap(doc).clear();
    getApprovalRequestsMap(doc).clear();
    getAutomationPoliciesMap(doc).clear();
    getExecutionRunsMap(doc).clear();
    getExecutionReceiptsMap(doc).clear();
    getContextFactsMap(doc).clear();
    getReviewReportsMap(doc).clear();
    getTaskArrangementsMap(doc).clear();
    getSelectedTaskArrangementsMap(doc).clear();
    getObjectivesMap(doc).clear();
    getInitiativesMap(doc).clear();
    getKeyResultsMap(doc).clear();
    getCheckInsMap(doc).clear();
    getProjectsMap(doc).clear();
    getAreasMap(doc).clear();
    getWorkflowColumnsMap(doc).clear();
    getSmartListsMap(doc).clear();
    getDeletedMap(doc).clear();
  });
}

export function ensureDefaultWorkflowColumns(doc: Y.Doc, now = Date.now()): void {
  const workflowColumns = getWorkflowColumnsMap(doc);

  for (const column of DEFAULT_WORKFLOW_COLUMNS) {
    if (workflowColumns.has(column.id)) continue;
    const inner = new Y.Map<unknown>();
    inner.set('name', column.name);
    inner.set('order', column.order);
    if (column.protected) inner.set('protected', true);
    inner.set('createdAt', now);
    inner.set('updatedAt', now);
    workflowColumns.set(column.id, inner);
  }
}
